import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface BulkOperationsProps {
  selectedItems: string[];
  onBulkEdit: (updates: any) => void;
  onBulkDelete: () => void;
  onExport: (format: 'csv' | 'json') => void;
  onImport: (data: any[]) => void;
  contentType: 'blog' | 'portfolio' | 'contacts' | 'consultations';
}

const BulkOperations: React.FC<BulkOperationsProps> = ({
  selectedItems,
  onBulkEdit,
  onBulkDelete,
  onExport,
  onImport,
  contentType,
}) => {
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [bulkEditData, setBulkEditData] = useState<any>({});
  const [importData, setImportData] = useState('');
  const [importFormat, setImportFormat] = useState<'csv' | 'json'>('json');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Remove handleSelectAll as it's handled in parent component

  const handleBulkEditSubmit = () => {
    if (selectedItems.length > 0) {
      onBulkEdit(bulkEditData);
      setShowBulkEdit(false);
      setBulkEditData({});
    }
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      try {
        let data: any[];
        if (file.name.endsWith('.csv')) {
          data = parseCSV(content);
        } else {
          data = JSON.parse(content);
        }
        onImport(data);
        setShowImport(false);
      } catch (error) {
        alert('Error parsing file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (csv: string): any[] => {
    const lines = csv.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim());
        const item: any = {};
        headers.forEach((header, index) => {
          item[header] = values[index] || '';
        });
        data.push(item);
      }
    }
    return data;
  };

  const handleTextImport = () => {
    try {
      let data: any[];
      if (importFormat === 'csv') {
        data = parseCSV(importData);
      } else {
        data = JSON.parse(importData);
      }
      onImport(data);
      setShowImport(false);
      setImportData('');
    } catch (error) {
      alert('Error parsing data. Please check the format.');
    }
  };

  const getBulkEditFields = () => {
    switch (contentType) {
      case 'blog':
        return [
          { key: 'category', label: 'Category', type: 'text' },
          { key: 'featured', label: 'Featured', type: 'checkbox' },
        ];
      case 'portfolio':
        return [
          { key: 'category', label: 'Category', type: 'text' },
          { key: 'featured', label: 'Featured', type: 'checkbox' },
        ];
      case 'contacts':
        return [
          { key: 'status', label: 'Status', type: 'select', options: ['new', 'contacted', 'closed'] },
        ];
      case 'consultations':
        return [
          { key: 'status', label: 'Status', type: 'select', options: ['pending', 'confirmed', 'completed', 'cancelled'] },
        ];
      default:
        return [];
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 sm:p-4 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={false}
              disabled={true}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-300">
              Selected: {selectedItems.length}
            </span>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {selectedItems.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowBulkEdit(true)}
                className="flex-1 sm:flex-initial px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <PencilIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Edit ({selectedItems.length})</span>
                <span className="sm:hidden">Edit</span>
              </button>
              <button
                onClick={() => {
                  if (confirm(`Are you sure you want to delete ${selectedItems.length} items?`)) {
                    onBulkDelete();
                  }
                }}
                className="flex-1 sm:flex-initial px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <TrashIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Delete ({selectedItems.length})</span>
                <span className="sm:hidden">Delete</span>
              </button>
            </div>
          )}
          
          <div className="flex gap-2">
            <button
              onClick={() => onExport('json')}
              className="flex-1 sm:flex-initial px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Export JSON</span>
              <span className="sm:hidden">JSON</span>
            </button>
            <button
              onClick={() => onExport('csv')}
              className="flex-1 sm:flex-initial px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">CSV</span>
            </button>
            <button
              onClick={() => setShowImport(true)}
              className="flex-1 sm:flex-initial px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <DocumentArrowUpIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Import</span>
              <span className="sm:hidden">Import</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEdit && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setShowBulkEdit(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-w-md w-full p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Bulk Edit ({selectedItems.length} items)
              </h3>
              <button
                onClick={() => setShowBulkEdit(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {getBulkEditFields().map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {field.label}
                  </label>
                  {field.type === 'text' && (
                    <input
                      type="text"
                      value={bulkEditData[field.key] || ''}
                      onChange={(e) => setBulkEditData({ ...bulkEditData, [field.key]: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`Update ${field.label.toLowerCase()}`}
                    />
                  )}
                  {field.type === 'checkbox' && (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={bulkEditData[field.key] || false}
                        onChange={(e) => setBulkEditData({ ...bulkEditData, [field.key]: e.target.checked })}
                        className="rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-700"
                      />
                      <span className="text-sm text-gray-300">Set as {field.label.toLowerCase()}</span>
                    </label>
                  )}
                  {field.type === 'select' && (
                    <select
                      value={bulkEditData[field.key] || ''}
                      onChange={(e) => setBulkEditData({ ...bulkEditData, [field.key]: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select {field.label}</option>
                      {(field as any).options?.map((option: string) => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button
                onClick={() => setShowBulkEdit(false)}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkEditSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Apply Changes
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Import Modal */}
      {showImport && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setShowImport(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Import Data</h3>
              <button
                onClick={() => setShowImport(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 sm:p-4 bg-yellow-500/20 border border-yellow-500/40 rounded-lg">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <div className="text-sm text-yellow-200">
                  <p className="font-medium">Import Warning</p>
                  <p>Importing data will add new items. Existing items with the same ID will be updated.</p>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-4">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 p-4 border-2 border-dashed border-gray-600 rounded-lg hover:border-gray-500 transition-colors text-center"
                >
                  <ArrowUpTrayIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium text-gray-300">Upload File</p>
                  <p className="text-xs text-gray-500">JSON or CSV format</p>
                </button>
                
                <div className="flex-1">
                  <div className="flex gap-2 mb-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="json"
                        checked={importFormat === 'json'}
                        onChange={(e) => setImportFormat(e.target.value as 'json')}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-gray-300">JSON</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="csv"
                        checked={importFormat === 'csv'}
                        onChange={(e) => setImportFormat(e.target.value as 'csv')}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-gray-300">CSV</span>
                    </label>
                  </div>
                  <textarea
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    placeholder={`Paste ${importFormat.toUpperCase()} data here...`}
                    className="w-full h-32 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono placeholder-gray-400"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button
                onClick={() => setShowImport(false)}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleTextImport}
                disabled={!importData.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                Import Data
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.csv"
              onChange={handleFileImport}
              className="hidden"
            />
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default BulkOperations;